import { Chip, Tooltip } from "@mui/material";
import {
  FolderOutlined,
  StorageOutlined,
  MenuBookOutlined,
  UploadFileOutlined,
  SmartToyOutlined,
} from "@mui/icons-material";

import type { SourceClassification } from "../../types/provenance";

const CLASSIFICATION_META: Record<
  SourceClassification,
  { label: string; color: "primary" | "success" | "info" | "warning" }
> = {
  project_file: { label: "Project file", color: "primary" },
  org_dataset: { label: "Org dataset", color: "success" },
  external_library: { label: "External library", color: "info" },
  adhoc_upload: { label: "Ad hoc upload", color: "warning" },
};

const CLASSIFICATION_ICON: Record<SourceClassification, React.ReactNode> = {
  project_file: <FolderOutlined sx={{ fontSize: "0.85rem" }} />,
  org_dataset: <StorageOutlined sx={{ fontSize: "0.85rem" }} />,
  external_library: <MenuBookOutlined sx={{ fontSize: "0.85rem" }} />,
  adhoc_upload: <UploadFileOutlined sx={{ fontSize: "0.85rem" }} />,
};

type SourceClassificationChipProps = {
  classification?: SourceClassification | null;
};

/**
 * Small chip naming where a source came from (Project file / Org dataset /
 * External library / Ad hoc upload). Rendered on Read and Cited items.
 */
export const SourceClassificationChip = ({
  classification,
}: SourceClassificationChipProps) => {
  if (!classification || !CLASSIFICATION_META[classification]) return null;
  const meta = CLASSIFICATION_META[classification];
  return (
    <Chip
      data-testid={`aa-src-classification-${classification}`}
      icon={CLASSIFICATION_ICON[classification] as React.ReactElement}
      label={meta.label}
      size="small"
      color={meta.color}
      variant="outlined"
      sx={{ height: "22px", fontSize: "0.7rem" }}
    />
  );
};

type AgentBadgeProps = {
  agent?: string | null;
};

/**
 * Badge showing which sub-agent retrieved a source (``retrieved_by_agent``).
 * Renders nothing when no agent is attributed.
 */
export const AgentBadge = ({ agent }: AgentBadgeProps) => {
  if (!agent) return null;
  return (
    <Tooltip title={`Retrieved by ${agent}`} arrow>
      <Chip
        data-testid="aa-src-agent-badge"
        icon={<SmartToyOutlined sx={{ fontSize: "0.85rem" }} />}
        label={agent}
        size="small"
        variant="outlined"
        sx={{
          height: "22px",
          fontSize: "0.7rem",
          color: "text.secondary",
          borderColor: "divider",
        }}
      />
    </Tooltip>
  );
};

export default SourceClassificationChip;
